import React from "react";

/**
 * A premium Pagination component for React applications.
 * 
 * @param {number} currentPage - The currently active page (1-indexed).
 * @param {number} totalItems - Total number of items across all pages.
 * @param {number} itemsPerPage - Number of items displayed per page.
 * @param {function} onPageChange - Callback function triggered when a page is selected.
 */
export default function Pagination({ currentPage, totalItems, itemsPerPage, onPageChange }) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisible - 1);

      if (end === totalPages) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) pages.push(i);

      if (start > 1) {
        if (start > 2) pages.unshift("...");
        pages.unshift(1);
      }

      if (end < totalPages) {
        if (end < totalPages - 1) pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const pages = getPageNumbers();

  return (
    <div className="pg-container">
      <div className="pg-info">
        Showing <span>{Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</span> to{" "}
        <span>{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span>{totalItems}</span> results
      </div>
      
      <div className="pg-controls">
        <button 
          className="pg-btn" 
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <div className="pg-numbers">
          {pages.map((p, idx) => (
            p === "..." ? (
              <span key={`dots-${idx}`} className="pg-dots">...</span>
            ) : (
              <button
                key={p}
                className={`pg-num-btn ${currentPage === p ? "active" : ""}`}
                onClick={() => onPageChange(p)}
              >
                {p}
              </button>
            )
          ))}
        </div>

        <button 
          className="pg-btn" 
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .pg-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem 2rem;
          background: #ffffff;
          border-top: 1px solid #f1f5f9;
          flex-wrap: wrap;
          gap: 1.5rem;
        }

        .pg-info {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
        }

        .pg-info span {
          color: #0f172a;
          font-weight: 700;
        }

        .pg-controls {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .pg-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .pg-btn:hover:not(:disabled) {
          border-color: #7c3aed;
          color: #7c3aed;
          background: #f5f3ff;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(124, 58, 237, 0.12);
        }

        .pg-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          background: #f8fafc;
        }

        .pg-numbers {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .pg-num-btn {
          min-width: 38px;
          height: 38px;
          padding: 0 0.5rem;
          border-radius: 10px;
          border: 1px solid transparent;
          background: transparent;
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }

        .pg-num-btn:hover:not(.active) {
          background: #f1f5f9;
          color: #0f172a;
        }

        .pg-num-btn.active {
          background: #7c3aed;
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);
        }

        .pg-dots {
          color: #cbd5e1;
          font-weight: 800;
          padding: 0 0.25rem;
        }

        @media (max-width: 640px) {
          .pg-container {
            justify-content: center;
            padding: 1.25rem 1rem;
          }
          .pg-info {
            width: 100%;
            text-align: center;
            order: 2;
          }
          .pg-controls {
            order: 1;
          }
        }
      `}} />
    </div>
  );
}
